import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';

import { useCloudSynced } from '@/hooks';

const KEY = 'awake';

const asBool = (v: unknown): boolean | null => (v === 'true' ? true : v === 'false' ? false : null);

export const canWakeLock = 'wakeLock' in navigator;

interface AwakeState {
  awake: boolean;
  setAwake: (on: boolean) => void;
}

const AwakeContext = createContext<AwakeState>({ awake: false, setAwake: () => {} });

export function useAwake(): AwakeState {
  return useContext(AwakeContext);
}

export function AwakeProvider({ children }: { children: ReactNode }) {
  const [awake, setAwake] = useCloudSynced<boolean>(KEY, asBool, false);
  const sentinel = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!canWakeLock || !awake) return;

    let stale = false;
    let pending = false;
    const acquire = () => {
      if (document.visibilityState !== 'visible' || pending) return;
      if (sentinel.current && !sentinel.current.released) return;
      pending = true;
      navigator.wakeLock
        .request('screen')
        .then((s) => {
          if (stale) void s.release();
          else sentinel.current = s;
        })
        .catch(() => {})
        .finally(() => {
          pending = false;
        });
    };

    acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => {
      stale = true;
      document.removeEventListener('visibilitychange', acquire);
      void sentinel.current?.release().catch(() => {});
      sentinel.current = null;
    };
  }, [awake]);

  return <AwakeContext.Provider value={{ awake, setAwake }}>{children}</AwakeContext.Provider>;
}
